import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Trophy,
  Heart,
  Loader2,
  AlertTriangle,
  AlertCircle,
  CheckCircle2,
  PackageX,
  Gift,
  History,
  Monitor,
  Settings,
  type LucideIcon,
} from "lucide-react";
import {
  adminMetrics,
  adminListProductos,
  adminGetSettings,
  type AdminMetrics,
  type AdminProducto,
  type AdminSettings,
} from "../../lib/api";
import { useAdminCtx } from "./AdminLayout";

// Inicio rediseñado. Antes eran 6 tarjetas de números + un párrafo de
// "pendientes de entrega" que el dueño dijo que no servía (nadie marca
// entregas, así que el estado "entregado" NO se usa). La página ahora
// arranca con lo accionable —alertas de configuración y stock por
// agotarse— y deja las métricas como una franja compacta secundaria.

export default function Dashboard() {
  const { user, sucursalIdEfectiva } = useAdminCtx();
  const [m24, setM24] = useState<AdminMetrics | null>(null);
  const [m7, setM7] = useState<AdminMetrics | null>(null);
  const [settings, setSettings] = useState<AdminSettings | null>(null);
  const [productos, setProductos] = useState<AdminProducto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      adminMetrics(1, sucursalIdEfectiva),
      adminMetrics(7, sucursalIdEfectiva),
      adminGetSettings(sucursalIdEfectiva),
      adminListProductos(sucursalIdEfectiva),
    ])
      .then(([d1, d7, s, prods]) => {
        setM24(d1);
        setM7(d7);
        setSettings(s);
        setProductos(prods);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [sucursalIdEfectiva]);

  if (loading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin opacity-50" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg bg-red-500/15 p-4 text-sm text-red-300">
        {error}
      </div>
    );
  }

  const alertas = settings ? buildAlertas(settings) : [];
  const stockBajo = computeStockBajo(productos);
  const isSuper = user.role === "super_admin";

  return (
    <div className="flex flex-col gap-6">
      <h1 className="sr-only">Inicio</h1>

      <AlertasSection alertas={alertas} />

      {/* Solo se muestra si hay productos por agotarse (computeStockBajo
          ya filtra activos con stock <= 3). */}
      {stockBajo.length > 0 && <StockBajoSection items={stockBajo} />}

      <MetricasFranja m24={m24} m7={m7} />

      <AccesosRapidos isSuper={isSuper} />
    </div>
  );
}

// ──────────────────────────────────────────────
// 1. Alertas accionables
// ──────────────────────────────────────────────

type AlertaTono = "rojo" | "amarillo";

type Alerta = {
  id: string;
  tono: AlertaTono;
  texto: string;
  to: string;
  cta: string;
};

// Deriva las alertas que aplican de los settings. Solo configuración que
// rompe o confunde la operación; nada de delivery.
function buildAlertas(s: AdminSettings): Alerta[] {
  const out: Alerta[] = [];

  if (!s.pin_configurado) {
    out.push({
      id: "pin",
      tono: "rojo",
      texto:
        "El PIN no está configurado. Sin PIN nadie puede desbloquear la pantalla.",
      to: "/admin/pantalla",
      cta: "Configurar PIN",
    });
  }

  if (s.modo_prueba) {
    out.push({
      id: "modo-prueba",
      tono: "amarillo",
      texto: "Modo prueba ACTIVO — las jugadas no se registran.",
      to: "/admin/pantalla",
      cta: "Ir a Mi pantalla",
    });
  }

  // Migration 038/042: habilitar/deshabilitar juegos pasó a ser GLOBAL
  // (super-admin, Ajustes del shopping). Antes esta alerta leía los flags
  // per-sucursal (ya muertos) y linkeaba a Mi pantalla (que ya no tiene el
  // toggle) -> quedó obsoleta y se quitó. El estado de los juegos se ve en
  // Ajustes del shopping › Juegos.

  return out;
}

function AlertasSection({ alertas }: { alertas: Alerta[] }) {
  if (alertas.length === 0) {
    // Estado tranquilo: discreto, no roba atención cuando todo está bien.
    return (
      <div className="flex items-center gap-2 text-sm opacity-50">
        <CheckCircle2 className="h-4 w-4" />
        Todo en orden.
      </div>
    );
  }

  return (
    <section className="flex flex-col gap-3" data-tour="dash-alertas">
      {alertas.map((a) => (
        <AlertaRow key={a.id} alerta={a} />
      ))}
    </section>
  );
}

function AlertaRow({ alerta }: { alerta: Alerta }) {
  const rojo = alerta.tono === "rojo";
  const Icon = rojo ? AlertCircle : AlertTriangle;
  // Paletas por tono — el rojo es "esto rompe", el amarillo es "ojo con esto".
  const box = rojo
    ? "border-red-500/40 bg-red-500/10 text-red-200"
    : "border-amber-400/40 bg-amber-400/10 text-amber-100";
  const cta = rojo
    ? "bg-red-500/20 hover:bg-red-500/30"
    : "bg-amber-400/20 hover:bg-amber-400/30";

  return (
    <div
      className={`flex flex-col gap-3 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between ${box}`}
    >
      <div className="flex items-start gap-2.5">
        <Icon className="mt-0.5 h-5 w-5 shrink-0" />
        <span className="text-sm font-medium">{alerta.texto}</span>
      </div>
      <Link
        to={alerta.to}
        className={`shrink-0 self-start rounded-lg px-3 py-1.5 text-sm font-bold transition sm:self-auto ${cta}`}
      >
        {alerta.cta}
      </Link>
    </div>
  );
}

// ──────────────────────────────────────────────
// 2. Stock por agotarse
// ──────────────────────────────────────────────

// Productos activos con stock finito y bajo. stock null = ilimitado (no
// es alerta). Orden ascendente para que lo más crítico (0) quede arriba.
const STOCK_BAJO_UMBRAL = 3;

function computeStockBajo(productos: AdminProducto[]): AdminProducto[] {
  return productos
    .filter(
      (p) =>
        p.activo === 1 && p.stock !== null && p.stock <= STOCK_BAJO_UMBRAL,
    )
    .sort((a, b) => (a.stock ?? 0) - (b.stock ?? 0));
}

function StockBajoSection({ items }: { items: AdminProducto[] }) {
  return (
    <section className="flex flex-col gap-3" data-tour="dash-stock">
      <h2 className="text-sm uppercase tracking-wider opacity-50">
        Stock por agotarse
      </h2>
      <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((p) => (
          <StockBajoRow key={p.id} producto={p} />
        ))}
      </ul>
    </section>
  );
}

function StockBajoRow({ producto }: { producto: AdminProducto }) {
  // Stock 0 con producto activo es el caso peligroso: si sale sorteado,
  // el juego falla por falta de premio. Lo resaltamos en rojo.
  const agotado = producto.stock === 0;
  const box = agotado
    ? "border-red-500/40 bg-red-500/10"
    : "border-white/10 bg-white/5 hover:bg-white/10";
  const badge = agotado
    ? "bg-red-500/30 text-red-100"
    : "bg-amber-400/20 text-amber-100";

  return (
    <li>
      <Link
        to="/admin/productos"
        className={`flex items-center justify-between gap-3 rounded-lg border px-4 py-3 transition ${box}`}
      >
        <span className="flex min-w-0 items-center gap-2.5">
          {agotado ? (
            <PackageX className="h-4 w-4 shrink-0 text-red-300" />
          ) : (
            <span
              aria-hidden
              className="h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ background: producto.color ?? "#a78bfa" }}
            />
          )}
          <span className="truncate font-medium">{producto.nombre}</span>
        </span>
        <span
          className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-bold ${badge}`}
        >
          {agotado ? "Sin stock" : `Quedan ${producto.stock}`}
        </span>
      </Link>
    </li>
  );
}

// ──────────────────────────────────────────────
// 3. Franja compacta de métricas (24h / 7d)
// ──────────────────────────────────────────────
// Más chica que las 6 tarjetas de antes. Sin "entregados": el estado de
// entrega no se usa, así que mostrarlo solo agrega ruido.

function MetricasFranja({
  m24,
  m7,
}: {
  m24: AdminMetrics | null;
  m7: AdminMetrics | null;
}) {
  return (
    <section className="flex flex-col gap-3" data-tour="dash-metricas">
      <h2 className="text-sm uppercase tracking-wider opacity-50">Actividad</h2>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <MetricCell
          icon={Trophy}
          label="Jugadas 24h"
          value={m24?.total ?? 0}
        />
        <MetricCell
          icon={Heart}
          label="Siga participando 24h"
          value={m24?.consolation ?? 0}
        />
        <MetricCell
          icon={Trophy}
          label="Jugadas 7d"
          value={m7?.total ?? 0}
        />
        <MetricCell
          icon={Heart}
          label="Siga participando 7d"
          value={m7?.consolation ?? 0}
        />
      </div>
    </section>
  );
}

function MetricCell({
  icon: Icon,
  label,
  value,
}: {
  icon: LucideIcon;
  label: string;
  value: number;
}) {
  return (
    <div className="rounded-lg bg-white/5 p-3">
      <div className="mb-1 flex items-center gap-1.5 opacity-50">
        <Icon className="h-4 w-4" />
        <span className="text-xs">{label}</span>
      </div>
      <div className="text-2xl font-bold">{value}</div>
    </div>
  );
}

// ──────────────────────────────────────────────
// 4. Accesos rápidos
// ──────────────────────────────────────────────

type Acceso = {
  to: string;
  icon: LucideIcon;
  label: string;
};

function AccesosRapidos({ isSuper }: { isSuper: boolean }) {
  const accesos: Acceso[] = [
    { to: "/admin/productos", icon: Gift, label: "Premios" },
    { to: "/admin/ganadores", icon: History, label: "Jugadas" },
    { to: "/admin/pantalla", icon: Monitor, label: "Mi pantalla" },
  ];
  // Ajustes del shopping es super_admin only (mismo gating que el sidebar).
  if (isSuper) {
    accesos.push({
      to: "/admin/shopping",
      icon: Settings,
      label: "Ajustes del shopping",
    });
  }

  return (
    <section className="flex flex-col gap-3" data-tour="dash-accesos">
      <h2 className="text-sm uppercase tracking-wider opacity-50">
        Accesos rápidos
      </h2>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {accesos.map((a) => (
          <AccesoRapidoCard key={a.to} acceso={a} />
        ))}
      </div>
    </section>
  );
}

function AccesoRapidoCard({ acceso }: { acceso: Acceso }) {
  const Icon = acceso.icon;
  return (
    <Link
      to={acceso.to}
      className="flex flex-col items-center justify-center gap-1.5 rounded-xl border border-white/10 bg-white/5 p-4 text-center transition hover:border-[#a78bfa]/40 hover:bg-white/10"
    >
      <Icon className="h-6 w-6 text-[#a78bfa]" />
      <span className="text-sm font-bold">{acceso.label}</span>
    </Link>
  );
}
